import { formatServerDateTime, Modal } from '@adiox/ui';
import { TOOL_LABELS, type Drawing } from './model';

const small = 'rounded border border-line px-2 py-0.5 text-xs hover:border-accent';

/**
 * MT5's Objects List (Ctrl+B): every drawing on this chart's symbol, where it starts, and a way to pick one out (it is
 * selected on the chart, ready to edit) or delete it.
 */
export function ObjectsList({
  open,
  symbol,
  drawings,
  digits,
  onSelect,
  onDelete,
  onDeleteAll,
  onClose,
}: {
  open: boolean;
  symbol: string;
  drawings: Drawing[];
  digits: number;
  onSelect: (id: string) => void;
  onDelete: (id: string) => void;
  onDeleteAll: () => void;
  onClose: () => void;
}) {
  return (
    <Modal size="md" open={open} title={`Objects on ${symbol}`} onClose={onClose}>
      {drawings.length === 0 ? (
        <p className="text-sm text-muted">
          There are no drawings on {symbol}. Add them from Draw on the chart toolbar.
        </p>
      ) : (
        <div className="flex flex-col gap-3">
          <div className="max-h-80 overflow-auto">
            <table className="w-full text-sm" aria-label="Objects">
              <thead>
                <tr className="border-b border-line text-left text-xs uppercase tracking-wide text-muted">
                  <th className="px-2 py-1 font-normal">Object</th>
                  <th className="px-2 py-1 font-normal">Starts at</th>
                  <th className="px-2 py-1 font-normal">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {drawings.map((d) => {
                  const first = d.points[0]!;
                  return (
                    <tr key={d.id} className="border-b border-line/40" data-testid="object-row">
                      <td className="px-2 py-1">
                        <span
                          className="mr-1.5 inline-block h-2.5 w-2.5 rounded-full"
                          style={{ background: d.color }}
                        />
                        {TOOL_LABELS[d.tool]}
                        {d.text ? <span className="text-muted"> “{d.text}”</span> : null}
                      </td>
                      <td className="num px-2 py-1 text-muted">
                        {/* anchors are server-clock times already */}
                        {formatServerDateTime(first.t * 1000, 0).slice(0, 16)} · {first.p.toFixed(digits)}
                      </td>
                      <td className="whitespace-nowrap px-2 py-1 text-right">
                        <button
                          type="button"
                          className={small}
                          onClick={() => {
                            onSelect(d.id);
                            onClose();
                          }}
                        >
                          Select
                        </button>{' '}
                        <button
                          type="button"
                          className={`${small} text-down`}
                          aria-label={`Delete ${TOOL_LABELS[d.tool]}`}
                          onClick={() => onDelete(d.id)}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="flex justify-between">
            <button type="button" className={`${small} text-down`} onClick={onDeleteAll}>
              Delete all…
            </button>
            <button type="button" className={small} onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}
